import { Search } from "@mui/icons-material";
import {
  Avatar,
  Box,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Modal,
  Stack
} from "@mui/material";
import { BackgroundInputField } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useAddTeamMember, useTeams } from "features/teams";
import Fuse from "fuse.js";
import { isEmpty, map } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { FixedSizeList } from "react-window";
import { useMembersStore } from "../stores";

export function AddTeamModal({ onSelect }) {
  const addTeamModalOpen = useMembersStore(state => state.addTeamModalOpen);
  const closeAddTeamModal = useMembersStore(state => state.closeAddTeamModal);
  const memberToAssignTeam = useMembersStore(state => state.memberToAssignTeam);
  const memberToAssignTeamTeams = useMembersStore(
    state => state.memberToAssignTeamTeams
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const addTeamMember = useAddTeamMember();
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const teamsQuery = useTeams({
    churchId: selectedChurch?.id
  });
  const [searchTerm, setSearchTerm] = useState("");
  const setMemberToAssignTeam = useMembersStore(
    state => state.setMemberToAssignTeam
  );
  const { pathname } = useLocation();

  const { t } = useTranslation();

  const isFilterMode = pathname.includes("/filter");

  // Teams the member already belongs to (only meaningful when assigning a single
  // member from the members table; empty in bulk /filter mode).
  const alreadyTeamIds = new Set(
    (memberToAssignTeamTeams ?? []).map(team => team?.id)
  );

  const isAlreadyJoined = teamId => !isFilterMode && alreadyTeamIds.has(teamId);

  useEffect(() => {
    setTeams(teamsQuery?.data?.result ?? []);
    setFilteredTeams(teamsQuery?.data?.result ?? []);
  }, [teamsQuery.data]);

  // Reset search state each time the modal opens so a stale search term or
  // empty filtered list from a previous open doesn't hide the team list.
  useEffect(() => {
    if (addTeamModalOpen) {
      setSearchTerm("");
      setFilteredTeams(teamsQuery?.data?.result ?? []);
    }
  }, [addTeamModalOpen]);

  const addTeam = teamId => {
    if (isFilterMode) {
      onSelect(teamId);
      return;
    }

    // Guard against re-adding a team the member is already in and against
    // double-submits while a request is in flight (prevents duplicate rows).
    if (isAlreadyJoined(teamId) || addTeamMember.isLoading) {
      return;
    }

    addTeamMember.mutate(
      {
        churchId: selectedChurch?.id,
        teamId: teamId,
        memberData: {
          member_ids: [memberToAssignTeam]
        }
      },
      {
        onSuccess: data => {
          if (!data?.status) {
            return;
          }
          closeAddTeamModal();
          setMemberToAssignTeam("");
        }
      }
    );
  };

  const searchTeams = () => {
    const fuse = new Fuse(teams || [], {
      threshold: 0.3,
      keys: ["name"]
    });

    if (isEmpty(searchTerm)) {
      setFilteredTeams(teams);
      return;
    }

    const searchResults = fuse.search(searchTerm);

    if (isEmpty(searchResults)) {
      setFilteredTeams([]);
    } else {
      setFilteredTeams([
        ...map(fuse.search(searchTerm), foundTerm => foundTerm.item)
      ]);
    }
  };

  return (
    <Modal open={addTeamModalOpen} onClose={closeAddTeamModal}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 24,
          borderRadius: 4,
          width: { xs: "95vw", md: "800px" },
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: "primary.main",
            color: "primary.contrastText"
          }}
        >
          {t("teams.teamProfile.AddTeamMember")}
        </Box>
        <Stack
          sx={{
            p: 4,
            backgroundColor: "white"
          }}
          spacing={2}
        >
          <BackgroundInputField
            variant="outlined"
            placeholder={t("teams.teams.SearchTeams")}
            sx={{
              backgroundColor: "neutral.main"
            }}
            neutral={true}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            onKeyUp={searchTeams}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <FixedSizeList
            height={400}
            itemSize={60}
            itemData={filteredTeams}
            itemCount={filteredTeams?.length || 0}
            overscanCount={5}
          >
            {props => {
              const { data, index, style } = props;
              const joined = isAlreadyJoined(data[index]["id"]);

              return (
                <ListItem style={style} key={index} sx={{ p: 0 }}>
                  <ListItemButton
                    sx={{ px: 4 }}
                    disabled={joined || addTeamMember.isLoading}
                    onClick={() => addTeam(data[index]["id"])}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={`${IMAGE_URL}/${data[index]["image_url"]}`}
                        alt={data[index]["name"]}
                        sx={{
                          outline: "2px solid",
                          outlineColor: "primary.main"
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={data[index]["name"]}
                      secondary={
                        joined ? t("teams.teamProfile.AlreadyAdded") : undefined
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            }}
          </FixedSizeList>
        </Stack>
      </Box>
    </Modal>
  );
}
